import { apiClient } from '../lib/api-client';
import type { CoreData, CoreDataResponse, User, Module } from '../types/core-data';

/**
 * Service for fetching core application data
 */
class CoreService {
  /**
   * Fetch all core data needed for the application
   * Includes departments, entities, german states, modules, and user data
   */
  async getCoreData(): Promise<CoreData> {
    console.log('[CoreService] Fetching core data from /v1/_core');
    
    const response = await apiClient.get<any>('/v1/_core');
    
    // API returns objects with numeric keys, convert to arrays
    const convertToArray = (obj: any): any[] => {
      if (!obj || typeof obj !== 'object') return [];
      return Object.values(obj);
    };
    
    // Modules is a key-value map, convert to array of module objects
    const parseModules = (modulesObj: any): Module[] => {
      if (!modulesObj || typeof modulesObj !== 'object') return [];
      return Object.entries(modulesObj).map(([name, enabled]) => ({
        id: 0, // API doesn't provide ID
        name,
        enabled: Boolean(enabled),
      }));
    };
    
    // Parse user data - API returns it as an object per OpenAPI spec
    // User object uses first_name and last_name (with underscores)
    const parseUser = (userData: any): User => {
      if (!userData) return {} as User;
      
      // If it's already an object, return it
      if (typeof userData === 'object' && !Array.isArray(userData)) {
        return userData as User;
      }
      
      // If it's a string, parse it (fallback for backwards compatibility)
      if (typeof userData === 'string') {
        try {
          return JSON.parse(userData) as User;
        } catch (e) {
          console.warn('[CoreService] Failed to parse user JSON string:', e);
          return {} as User;
        }
      }
      
      return {} as User;
    };
    
    const coreData: CoreData = {
      departments: convertToArray(response.data.departments),
      entities: convertToArray(response.data.entities),
      germanStates: convertToArray(response.data.german_states),
      modules: parseModules(response.data.modules),
      user: parseUser(response.data.user),
    };

    console.log('[CoreService] Core data loaded:', {
      departmentsCount: coreData.departments.length,
      entitiesCount: coreData.entities.length,
      statesCount: coreData.germanStates.length,
      modulesCount: coreData.modules.length,
      modulesEnabled: coreData.modules.filter(m => m.enabled).length,
      user: coreData.user.username || 'N/A',
    });

    return coreData;
  }

}

export const coreService = new CoreService();

